import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAttendanceStore } from '../stores/attendanceStore';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StudentSearch from '../components/attendance/StudentSearch';

const AttendancePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    students,
    selectedStudent,
    setSelectedStudent,
    updateStudentParking,
    recordAttendance,
  } = useAttendanceStore();

  const [parkingNumber, setParkingNumber] = useState('');
  const [hasParkingSpace, setHasParkingSpace] = useState(false);
  const [parkingFeedback, setParkingFeedback] = useState('');
  const [isParkingConfirmed, setIsParkingConfirmed] = useState(false);

  const handleStudentSelect = (student: any) => {
    setSelectedStudent(student);
    if (student) {
      setParkingNumber(student.parkingNumber || '');
      setHasParkingSpace(student.hasParkingSpace || false);
      setIsParkingConfirmed(false);
      setParkingFeedback('');
    } else {
      setParkingNumber('');
      setHasParkingSpace(false);
      setIsParkingConfirmed(false);
      setParkingFeedback('');
    }
  };

  const handleParkingToggle = () => {
    setHasParkingSpace(!hasParkingSpace);
    if (!hasParkingSpace) {
      setParkingNumber('');
      setParkingFeedback('');
      setIsParkingConfirmed(false);
    }
  };

  const handleParkingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setParkingNumber(value);
    setIsParkingConfirmed(false);
    setParkingFeedback('');
  };

  const handleConfirmParking = () => {
    if (!parkingNumber.trim()) {
      setParkingFeedback('차량번호를 입력해주세요! ⚠️');
      return;
    }
    
    // 차량번호 형식 검증 (숫자 + 한글 + 숫자 형식)
    const parkingPattern = /^\d{2,3}[가-힣]\d{4}$/;
    if (!parkingPattern.test(parkingNumber.trim())) {
      setParkingFeedback('올바른 차량번호 형식이 아닙니다! (예: 12가3456) ⚠️');
      return;
    }
    
    setParkingFeedback('접수되었습니다. 다음으로 랜덤테이블 번호를 배정받으세요. ✅');
    setIsParkingConfirmed(true);
  };

  const handleTableAssignment = () => {
    if (!selectedStudent) {
      alert('학생을 먼저 선택해주세요.');
      return;
    }

    if (hasParkingSpace && !isParkingConfirmed) {
      alert('차량번호를 확인해주세요.');
      return;
    }

    // 주차 정보 업데이트
    updateStudentParking(selectedStudent.id, parkingNumber, hasParkingSpace);
    
    // 출석 기록
    recordAttendance(selectedStudent.id, parkingNumber, hasParkingSpace);
    
    // 테이블 배정 페이지로 이동
    navigate('/table-assignment');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-2xl mx-auto relative">
        {/* 홈으로 버튼 - 우측 상단 */}
        <button
          onClick={handleGoHome}
          className="absolute top-0 -right-32 px-6 py-3 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
        >
          🏠 홈으로
        </button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 헤더 */}
          <Card className="mb-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">📚 KGLA 원리교육 출석체크</h1>
              <p className="text-gray-600">연구원님의 성함을 검색하고 출석을 기록하세요</p>
            </div>
          </Card>

          {/* 학생 검색 */}
          <Card className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">🔍 이름 검색</h2>
            <StudentSearch
              students={students}
              onStudentSelect={handleStudentSelect}
              selectedStudent={selectedStudent}
            />
          </Card>

          {/* 주차 정보 */}
          {selectedStudent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">🚗 주차 정보</h2>
                
                {/* 주차 여부 토글 */}
                <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">☑️</span>
                    <span className="text-lg font-medium text-gray-700">주차 여부</span>
                  </div>
                  <button
                    onClick={handleParkingToggle}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                      hasParkingSpace ? 'bg-success' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-md ${
                        hasParkingSpace ? 'translate-x-7' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* 차량번호 입력 */}
                {hasParkingSpace && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="space-y-3 p-3 bg-gray-50 rounded-xl"
                  >
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">🔢</span>
                        <label className="text-lg font-medium text-gray-700">차량번호</label>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={parkingNumber}
                          onChange={handleParkingNumberChange}
                          placeholder="예: 12가3456"
                          className="input-field flex-1 text-base py-2 px-3 rounded-xl"
                          maxLength={10}
                        />
                        <button
                          onClick={handleConfirmParking}
                          className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 text-sm"
                        >
                          확인
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        숫자 + 한글 + 숫자 형식으로 입력해주세요 (예: 12가3456). 띄워쓰기 없이 입력해주세요.
                      </p>
                    </div>
                    
                    {/* 피드백 메시지 */}
                    {parkingFeedback && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`rounded-lg p-3 border ${
                          parkingFeedback.includes('✅') 
                            ? 'bg-success/20 border-success/30' 
                            : 'bg-yellow-50 border-yellow-300'
                        }`}
                      >
                        <p className={`font-medium text-center text-sm ${
                          parkingFeedback.includes('✅') 
                            ? 'text-success' 
                            : 'text-yellow-700'
                        }`}>
                          {parkingFeedback}
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </Card>
            </motion.div>
          )}

          {/* 테이블 배정하기 버튼 */}
          <div className="mt-8">
            <button
              onClick={handleTableAssignment}
              disabled={!selectedStudent}
              className="w-full px-8 py-6 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold text-xl rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg disabled:cursor-not-allowed"
            >
              🎯 테이블 배정하기
            </button>
          </div>

          {/* 안내 메시지 */}
          {!selectedStudent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6"
            >
              <Card className="bg-blue-50 border-blue-200">
                <div className="text-blue-800 text-center">
                  <p className="font-medium mb-2">💡 사용 방법</p>
                  <p className="text-sm">
                    1. 위 검색창에 학생 이름을 입력하세요<br />
                    2. 초성 검색도 가능합니다 (예: ㄱㅁㅅ → 김민서)<br />
                    3. 학생을 선택하면 주차 정보를 입력할 수 있습니다<br />
                    4. 테이블 배정하기 버튼을 눌러 랜덤 테이블을 배정받으세요
                  </p>
                </div>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AttendancePage; 